import React, { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Clock3 } from "lucide-react";

import ViewContainer from "../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";

import SecondaryPillTabs from "../../../../components/tabs/SecondaryPillTabs";
import Button from "../../../../components/buttons/Button";
import EmptyState from "../../../../components/feedback/EmptyState";
import ContentGrid from "../../../../components/grids/content/ContentGrid";
import VideoThumbnailCard from "../../../../components/cards/thumbnails/VideoThumbnailCard";

import { useMyVideoRentals } from "../hooks/useVideoRental";
import { organizeRentals } from "../../utils/rentalUtils";

import "./MyRentalsView.css";

const TABS = [
  { id: "active", label: "Active" },
  { id: "expired", label: "Expired" },
];

const GRID_CONFIG = {
  columns: 4,
  tabletColumns: 3,
  mobileColumns: 2,
  gap: 16,
  showEmpty: false,
};

export default function MyRentalsView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { rentals = [], loading, error, refreshRentals } = useMyVideoRentals();

  const activeTab =
    searchParams.get("tab") === "expired" ? "expired" : "active";

  const { activeVideos, expiredVideos } = useMemo(
    () => organizeRentals(rentals),
    [rentals],
  );

  const tabs = [
    {
      id: "active",
      label: `Active (${activeVideos.length})`,
    },
    {
      id: "expired",
      label: `Expired (${expiredVideos.length})`,
    },
  ];

  const videos = activeTab === "active" ? activeVideos : expiredVideos;

  const handleTabChange = (selected) => {
    const tab =
      typeof selected === "string" ? selected : selected?.id || selected?.value;

    if (!TABS.some((item) => item.id === tab)) return;

    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);

      params.set("tab", tab);

      return params;
    });
  };

  const handleVideoClick = (video) => {
    if (!video?.videoTag) return;

    navigate(`/watch/${encodeURIComponent(video.videoTag)}`);
  };

  const handleRefresh = () => {
    if (!loading) {
      void refreshRentals();
    }
  };

  const renderVideo = (video) => (
    <VideoThumbnailCard
      key={video.videoId || video.videoTag}
      video={video}
      onClick={() => handleVideoClick(video)}
    />
  );

  const errorMessage =
    typeof error === "string"
      ? error
      : error?.message || "Unable to load rentals.";

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title="My Videos"
          subTitle="Your active and previous video rentals."
        />

        <SecondaryPillTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={handleTabChange}
        />

        <br />

        {error && (
          <div className="my-rentals__error" role="alert">
            <p>{errorMessage}</p>

            <Button
              value="Try Again"
              variant="primary-outlined"
              onClick={handleRefresh}
              disabled={loading}
            />
          </div>
        )}

        {loading && videos.length === 0 ? (
          <ContentGrid
            {...GRID_CONFIG}
            items={[]}
            loading
            skeletonCount={8}
            renderItem={() => null}
          />
        ) : videos.length === 0 ? (
          <EmptyState
            icon={<Clock3 size={28} />}
            title={
              error
                ? "Unable to load rentals"
                : activeTab === "active"
                  ? "No active rentals"
                  : "No expired rentals"
            }
            description={
              error
                ? errorMessage
                : activeTab === "active"
                  ? "Your active video rentals will appear here."
                  : "Your previous rentals will appear here."
            }
          />
        ) : (
          <ContentGrid
            {...GRID_CONFIG}
            items={videos}
            renderItem={renderVideo}
          />
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
